// PentestApiExploits.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, Typography, Card, TextField, Button, Stack, Grid, IconButton, Tooltip,
  LinearProgress
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterListIcon from '@mui/icons-material/FilterList';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate, useParams } from 'react-router-dom';
import { alpha , darken, lighten} from '@mui/material/styles';
import { BASEURL ,PRIMARYCOLOR,SECONDARYCOLOR,logoURL,COMPANY } from '../../Constants';
import Nav from './nav';
import ExploitsModal from './exploitsModal'; 

const sevColor = {
  critical:'#800080', high:'#f44336', medium:'#ff9800', low:'#2196f3', info:'#4caf50'
};
const methodColor = {
  GET:'#90caf9', POST:'#a5d6a7', PUT:'#ffe082', DELETE:'#ffab91', PATCH:'#ce93d8'
};

const getSeverityColor = (sev) => sevColor[sev?.toLowerCase?.()] || '#9e9e9e';
const getMethodColor = (m) => methodColor[m?.toUpperCase?.()] || '#e0e0e0';

const severityOrder = ['critical', 'high', 'medium', 'low', 'info'];
const sevSortVal = (s) => severityOrder.indexOf((s || '').toLowerCase());

const parseLocation = (loc) => {
  if (!loc) return { path: '', method: '' };
  const parts = String(loc).split(' - ');
  if (parts.length === 2) return { path: parts[0], method: parts[1].toUpperCase() };
  const m = String(loc).match(/(.*)\s+-\s+([A-Z]+)$/i);
  if (m) return { path: m[1], method: m[2].toUpperCase() };
  return { path: String(loc), method: '' };
};

const NiceBarRow = ({ label, value = 0, max = 1, onClick }) => {
  const pct = Math.min(100, Math.round((value / Math.max(1, max)) * 100));
  return (
    <Stack spacing={0.5} sx={{ cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 600 }} title={label}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{value}</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 999 }} />
    </Stack>
  );
};

const PentestApiExploits = () => {
  const [toolsData, setToolsData] = useState([]);
  const [search, setSearch] = useState('');
  const [sevFilter, setSevFilter] = useState('ALL'); // ALL | critical | high | ...
  const [selectedExploit, setSelectedExploit] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const navigate = useNavigate();
  const { pentest_id } = useParams();

  // Fetch exploits data
  const getTools = (token) => {
    fetch(`${BASEURL}/pentest/exploits?pentest_id=${pentest_id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error && data.message === 'Invalid token') {
          navigate('/');
        } else {
          setToolsData(Array.isArray(data.exploits) ? data.exploits : []);
        }
      })
      .catch((err) => console.error('Error fetching data:', err));
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) getTools(token);
    else navigate('/');
  }, [navigate, pentest_id]);

  // Derived data / stats
  const rows = useMemo(() => {
    return (toolsData || []).map((r) => {
      const { path, method } = parseLocation(r.impacted_assets);
      return { ...r, _path: path, _method: method };
    });
  }, [toolsData]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((item) => {
      const matchesSev = sevFilter === 'ALL' ? true : (item.severity || '').toLowerCase() === sevFilter;
      const matchesText =
        !q ||
        (item.name || '').toLowerCase().includes(q) ||
        (item.severity || '').toLowerCase().includes(q) ||
        (item._path || '').toLowerCase().includes(q) ||
        (item._method || '').toLowerCase().includes(q);
      return matchesSev && matchesText;
    });
  }, [rows, search, sevFilter]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => sevSortVal(a.severity) - sevSortVal(b.severity));
  }, [filtered]);

  const sevCounts = useMemo(() => {
    const out = { critical:0, high:0, medium:0, low:0, info:0 };
    rows.forEach((r) => {
      const s = (r.severity || '').toLowerCase();
      if (s in out) out[s]++;
    });
    return out;
  }, [rows]);

  const countByEndpoint = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      const key = r._path || '—';
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map)
      .map(([k, v]) => ({ key: k, value: v }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [rows]);
  const countByFinding = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      const key = r.name || '—';
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map)
      .map(([k, v]) => ({ key: k, value: v }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [rows]);

  const maxEndpointCount = Math.max(1, ...countByEndpoint.map((x) => x.value));
  const maxFindingCount = Math.max(1, ...countByFinding.map((x) => x.value));

  // Modal handlers
  const handleViewDetails = (exploit) => {
    setSelectedExploit(exploit);
    setModalOpen(true);
  };
  const handleCloseModal = () => {
    setSelectedExploit(null);
    setModalOpen(false);
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch {}
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f7f9fc' }}>
      <Nav />

      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, overflowY: 'auto' }}>
        {/* Header */}
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2 }}>Findings</Typography>
            <Typography variant="body2" color="text.secondary">
              All findings detected by the copilot. Click a row to view details.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, md: 0 } }}>
            <TextField
              size="small"
              placeholder="Search findings, severity, path, method…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Tooltip title="Filter by severity">
              <IconButton onClick={() => setSevFilter(sevFilter === 'ALL' ? 'high' : 'ALL')}>
                <FilterListIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Stats */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {['critical','high','medium','low','info'].map((s) => (
            <Grid item xs={6} sm={6} md={2.4} key={s}>
              <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
                <Typography variant="overline" color="text.secondary">{s.toUpperCase()}</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" sx={{ bgcolor: getSeverityColor(s), color:'#fff', fontWeight: 700 }} label={s} />
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>{sevCounts[s]}</Typography>
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Mini charts */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
              <Typography variant="overline" color="text.secondary">TOP 5 IMPACTED ENDPOINTS</Typography>
              <Stack spacing={1.25} sx={{ mt: 1 }}>
                {countByEndpoint.map((row) => (
                  <NiceBarRow
                    key={row.key}
                    label={row.key}
                    value={row.value}
                    max={maxEndpointCount}
                    onClick={() => setSearch(row.key)}
                  />
                ))}
                {countByEndpoint.length === 0 && <Typography variant="body2" color="text.secondary">No data</Typography>}
              </Stack>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
              <Typography variant="overline" color="text.secondary">TOP 5 FINDINGS</Typography>
              <Stack spacing={1.25} sx={{ mt: 1 }}>
                {countByFinding.map((row) => (
                  <NiceBarRow
                    key={row.key}
                    label={row.key}
                    value={row.value}
                    max={maxFindingCount}
                    onClick={() => setSearch(row.key)}
                  />
                ))}
                {countByFinding.length === 0 && <Typography variant="body2" color="text.secondary">No data</Typography>}
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* Filter row */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary">SEVERITY FILTER</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {['ALL','critical','high','medium','low','info'].map((s) => (
              <Chip
                key={s}
                size="small"
                label={s.toUpperCase()}
                onClick={() => setSevFilter(s)}
                sx={{
                  bgcolor: sevFilter === s ? (s==='ALL' ? '#e0e0e0' : getSeverityColor(s)) : 'transparent',
                  color: sevFilter === s ? (s==='ALL' ? '#000' : '#fff') : 'inherit',
                  border: '1px solid #e0e0e0'
                }}
              />
            ))}
          </Stack>
        </Stack>

        {/* Findings table */}
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 2 }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: PRIMARYCOLOR }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Severity</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Finding</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Method</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white' }} align="right">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.length > 0 ? (
                sorted.map((row, index) => {
                  const sev = (row.severity || '').toLowerCase();
                  const displaySeverity = sev.charAt(0).toUpperCase() + sev.slice(1);
                  return (
                    <TableRow
                      key={index}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleViewDetails(row)}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.created_at}</TableCell>
                      <TableCell>
                        <Chip label={displaySeverity} sx={{ bgcolor: getSeverityColor(sev), color: '#fff' }} size="small" />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 420 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.name}</Typography>
                        {row.cwe && (
                          <Typography variant="caption" color="text.secondary">CWE: {row.cwe}</Typography>
                        )}
                      </TableCell>
                      <TableCell title={row._path}>
                        <Typography variant="body2" noWrap>{row._path || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        {row._method ? (
                          <Chip size="small" label={row._method} sx={{ bgcolor: getMethodColor(row._method) }} />
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Button variant="outlined" size="small" onClick={(e) => { e.stopPropagation(); handleViewDetails(row); }}>
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">No data available</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* New polished modal */}
      <ExploitsModal
        open={modalOpen}
        handleClose={handleCloseModal}
        exploit={selectedExploit}
      />
    </Box>
  );
};

export default PentestApiExploits;
